import { useSyncExternalStore } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import * as Toast from '@radix-ui/react-toast';

interface Item {
  id: number;
  text: string;
}

let items: Item[] = [];
let next = 1;
let host: { root: Root; el: HTMLElement } | null = null;
const listeners = new Set<() => void>();

function set(list: Item[]) {
  items = list;
  listeners.forEach((l) => l());
}

/**
 * A short confirmation that does not move the page or need dismissing, in
 * place of "Saved." text beside a button. Mounts its own host on first use, like
 * `askConfirm`, so a caller needs no provider. Showing the same words again
 * replaces the earlier one instead of stacking.
 */
export function toast(text: string) {
  if (!host) {
    const el = document.body.appendChild(document.createElement('div'));
    host = { root: createRoot(el), el };
    host.root.render(<Toaster />);
  }
  set([...items.filter((i) => i.text !== text), { id: next++, text }]);
}

/**
 * Take every toast and the host away. Unmounting cancels each toast's own close timer, so
 * none fires later; the tests call this after each one (src/test-setup.ts), since a timer
 * outliving its test file finds no page left to close.
 */
export function clearToasts() {
  host?.root.unmount();
  host?.el.remove();
  host = null;
  items = [];
}

function Toaster() {
  const list = useSyncExternalStore(
    (cb) => (listeners.add(cb), () => void listeners.delete(cb)),
    () => items,
  );
  return (
    <Toast.Provider duration={4000} label="Notifications">
      {list.map((i) => (
        <Toast.Root
          key={i.id}
          className="toast"
          onOpenChange={(open) => !open && set(items.filter((x) => x.id !== i.id))}
        >
          <Toast.Description>{i.text}</Toast.Description>
        </Toast.Root>
      ))}
      <Toast.Viewport className="toast-viewport" />
    </Toast.Provider>
  );
}
